import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  InjectionToken,
  isDevMode,
  Signal,
} from '@angular/core';
import { ButtonComponent, ModalComponent } from 'core-ui';

import { TranslationKey } from '../../consts/translation-key.type';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { describeServerError, ServerErrorDetails } from '../../services/server-error-details';
import {
  RETRY_DELAY_SECONDS,
  ServerErrorRetryService,
} from '../../services/server-error-retry.service';
import { t } from '../../services/translate';
import { CountdownTimerComponent } from '../countdown-timer/countdown-timer.component';
import { PistonLoaderComponent } from '../piston-loader/piston-loader.component';

/** Raw backend errors in the modal are a local-development aid only; never in a release build. */
export const SHOW_SERVER_ERROR_DETAILS = new InjectionToken<boolean>('SHOW_SERVER_ERROR_DETAILS', {
  factory: (): boolean => isDevMode(),
});

/**
 * Blocking modal over the whole app while a 5xx retry-cycle runs. Deliberately not
 * dismissable (no backdrop/esc; the header X is hidden in _post.scss).
 */
@Component({
  selector: 'wpp-server-error-modal',
  // core-ui ships a component with this same selector; the extra host attribute keeps the
  // two component ids apart (NG0912).
  host: { 'data-wpp-planner': '' },
  imports: [
    ModalComponent,
    ButtonComponent,
    CountdownTimerComponent,
    PistonLoaderComponent,
    TranslatePipe,
  ],
  templateUrl: './server-error-modal.component.html',
  styleUrl: './server-error-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServerErrorModalComponent {
  private readonly serverErrorRetry = inject(ServerErrorRetryService);
  private readonly document = inject(DOCUMENT);
  private readonly showErrorDetails: boolean = inject(SHOW_SERVER_ERROR_DETAILS);

  protected readonly retryDelaySeconds: number = RETRY_DELAY_SECONDS;
  protected readonly phase = this.serverErrorRetry.phase;
  protected readonly secondsLeft = this.serverErrorRetry.secondsLeft;

  protected readonly open: Signal<boolean> = computed((): boolean => this.phase() !== 'idle');

  protected readonly body: Signal<TranslationKey> = computed((): TranslationKey => {
    switch (this.phase()) {
      case 'retrying':
        return 'wpp.component.server-error-modal.body-retrying';
      case 'failed':
        return 'wpp.component.server-error-modal.body-failed';
      default:
        return 'wpp.component.server-error-modal.body-counting';
    }
  });

  protected readonly errorDetails: Signal<ServerErrorDetails[]> = computed(
    (): ServerErrorDetails[] =>
      this.showErrorDetails ? this.serverErrorRetry.errors().map(describeServerError) : [],
  );

  protected readonly title: Signal<string> = computed((): string =>
    this.phase() === 'failed'
      ? t('wpp.component.server-error-modal.title-failed')
      : t('wpp.common.error.title'),
  );

  protected reloadPage(): void {
    this.document.defaultView?.location.reload();
  }
}
